import { useEffect, useState } from "react";
import { accountApi } from "../../services/accountApi";
import { useToast } from "../../context/ToastContext";
import { Button } from "../../components/common/Button";
import { Card } from "../../components/common/Card";
import { Field } from "../../components/common/Field";

/**
 * The number an inspector asks for (#274).
 *
 * V012 built the regulatory reports and left them anonymous, so a
 * treatment history printed for an inspector was a spreadsheet rather
 * than a filing. This is the field that finishes it.
 *
 * Ungated on purpose. Registration is a legal obligation in several
 * states, and putting it behind a paid tier would mean charging for
 * compliance.
 */
export function RegistrationTab() {
  const { notify } = useToast();
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    accountApi
      .getApiary()
      .then((r) => {
        if (cancelled) return;
        setName(r.apiary_name || "");
        setValue(r.apiary_registration || "");
      })
      .catch(() => !cancelled && notify("Couldn't load your registration number", "warning"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    setSaving(true);
    try {
      const r = await accountApi.setApiary({ apiary_name: name, apiary_registration: value });
      setName(r.apiary_name || "");
      setValue(r.apiary_registration || "");
      // Says which outcome happened. Clearing it is a deliberate act --
      // somebody whose registration lapsed should see that it took.
      notify(
        r.apiary_registration ? "Saved" : "Saved — registration number cleared",
        "success",
      );
    } catch (err) {
      notify(err.apiMessage || "Couldn't save that just now", "danger");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="About this apiary">
      <Field
        label="Apiary name"
        hint="Shown to anyone helping at your hives, instead of your email address."
      >
        {(id, aria) => (
          <input
            id={id}
            {...aria}
            className="touch-target"
            value={name}
            disabled={loading}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder={loading ? "Loading…" : "e.g. Abilene Home Apiary"}
          />
        )}
      </Field>

      <p style={{ color: "var(--color-text-muted)" }}>
        Several states require beekeepers to register, and issue a number that
        has to appear on the records you file. It goes on every regulatory
        report this app produces.
      </p>

      <Field
        label="Registration number"
        hint="However your state writes it. Leave empty if you aren't registered."
      >
        {(id, aria) => (
          <input
            id={id}
            {...aria}
            className="touch-target"
            value={value}
            disabled={loading}
            maxLength={60}
            onChange={(e) => setValue(e.target.value)}
            placeholder={loading ? "Loading…" : "e.g. NC-2026-1184"}
          />
        )}
      </Field>

      <Button onClick={save} disabled={loading || saving}>
        {saving ? "Saving…" : "Save"}
      </Button>

      {/* Said here rather than only at filing time. Finding out the number
          is missing while an inspector waits is the wrong moment. */}
      {!loading && !value && (
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          Reports will print without a registration number until this is set.
        </p>
      )}
    </Card>
  );
}
